import { forwardRef, useId } from 'react'
import type { InputHTMLAttributes, ReactNode, TextareaHTMLAttributes } from 'react'
import { cn } from '@/lib/cn'

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix'> {
  label?: string
  /** Podpis pod polem (wskazówka). Ukrywany, gdy jest `error`. */
  hint?: string
  error?: string | null
  /** Ikona/prefiks wewnątrz pola po lewej. */
  prefix?: ReactNode
  /** Element po prawej (np. skrót ⌘K). */
  suffix?: ReactNode
  block?: boolean
  /** Monospace — sygnatury, kwoty. */
  mono?: boolean
}

export const fieldBaseClass =
  'h-[34px] w-full rounded border bg-card px-3 text-base text-ink placeholder:text-muted/70 ' +
  'transition-colors duration-fast focus:outline-none focus:border-navy-600 focus:am-focus ' +
  'disabled:bg-[#f4f6f8] disabled:text-muted disabled:cursor-not-allowed'

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, prefix, suffix, block = true, mono = false, className, id, ...rest },
  ref,
) {
  const autoId = useId()
  const inputId = id ?? autoId
  return (
    <div className={cn(block ? 'w-full' : 'inline-block')}>
      {label ? (
        <label htmlFor={inputId} className="t-label mb-1.5 block">
          {label}
        </label>
      ) : null}
      <div className="relative">
        {prefix ? (
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted">{prefix}</span>
        ) : null}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          className={cn(
            fieldBaseClass,
            error ? 'border-danger' : 'border-line',
            prefix && 'pl-8',
            suffix && 'pr-12',
            mono && 'font-mono text-small',
            className,
          )}
          {...rest}
        />
        {suffix ? (
          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted">{suffix}</span>
        ) : null}
      </div>
      {error ? (
        <p className="mt-1 text-micro text-danger">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-micro text-muted">{hint}</p>
      ) : null}
    </div>
  )
})

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  hint?: string
  error?: string | null
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, className, id, rows = 4, ...rest },
  ref,
) {
  const autoId = useId()
  const fieldId = id ?? autoId
  return (
    <div className="w-full">
      {label ? (
        <label htmlFor={fieldId} className="t-label mb-1.5 block">
          {label}
        </label>
      ) : null}
      <textarea
        ref={ref}
        id={fieldId}
        rows={rows}
        className={cn(
          fieldBaseClass,
          'h-auto py-2 leading-relaxed',
          error ? 'border-danger' : 'border-line',
          className,
        )}
        {...rest}
      />
      {error ? (
        <p className="mt-1 text-micro text-danger">{error}</p>
      ) : hint ? (
        <p className="mt-1 text-micro text-muted">{hint}</p>
      ) : null}
    </div>
  )
})
